import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'

import saleControlApi, { type SaleControlFinalizedFilter } from '../../store/api/saleControlApi'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useDeleteSaleAllocationMutation } from '../../store/api/saleAllocationApi'
import { getErrorMessage } from '../../utils/error'
import SalesAllocationFormDialog from './SalesAllocationFormDialog'
import SalesAllocationMassUploadDialog from './SalesAllocationMassUploadDialog'
import SalesAllocationEditSameDoDialog from './SalesAllocationEditSameDoDialog'
import SalesAllocationEditBookingDialog from './SalesAllocationEditBookingDialog'
import SalesAllocationVgmPrintDialog from './SalesAllocationVgmPrintDialog'
import SalesAllocationBbgnPrintDialog from './SalesAllocationBbgnPrintDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

const { useGetCtrlSalesSaleAlcQuery } = saleControlApi

type FilterKind = 'search'
type FinalizedFilter = SaleControlFinalizedFilter

/**
 * Sales Allocation row — keys mirror `frmCtrlSalesQrySaleAlc` `ControlSource`
 * (snake_case, the column names the Access query view exposes). The primary key
 * `sales_allocation_scs_id` (referenced by the Access main form `Form![subForm]!…`)
 * drives row selection; it is not a datasheet column.
 */
interface SaleAllocationRow {
  sales_allocation_scs_id: number
  sale_contract: string | null
  delivery_order: string | null
  booking_no: string | null
  client: string | null
  contract_quality: string | null
  m_date: string | null
  from_warehouse: string | null
  to_warehouse: string | null
  m_truck_no: string | null
  m_net_weight: number | null
  container_no: string | null
  seal_no: string | null
  truck_name: string | null
  number_bags: number | null
  tare_weight: number | null
  gross_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
  max_gross_weight: number | null
  vgm: number | null
  receipt_no: string | null
  gdn_or_rdn: string | null
  vgm_date: string | null
  from_supplier: string | null
  to_client: string | null
  receipt_quality: string | null
  net_delivered_weight: number | null
  no_bag_delivered: number | null
  net_weight_at_buyer: number | null
  deduction_at_buyer_kg: number | null
  deduction_at_buyer_vnd: number | null
  payment_weight: number | null
  accumulate_allocated_bag: number | null
  accumulate_allocated_kg: number | null
  can_be_allocated_bag: number | null
  can_be_allocated_kg: number | null
  allocating_bag: number | null
  allocating_quantity: number | null
  balance_bag: number | null
  balance_kg: number | null
  weight_loss: number | null
  note: string | null
  date_update: string | null
  person_update: string | null
}

/** Freeze the Contract No. identifier column when scrolling horizontally (skill /table-style §7.6). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['sale_contract'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { sale_contract: 130 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { sale_contract: 110 }

/** TablePagination «All» maps to this pageSize (single request). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof SaleAllocationRow
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + captions follow Access `frmCtrlSalesQrySaleAlc` datasheet
 * (definition order = datasheet order; `Label.Caption` verbatim). Per-user
 * column visibility (Access `Form_Open` reads `public_global_filter.ColumnVisibility`)
 * maps to the web Columns menu (`usePreferenceColumns`). Dates `d-mmm-yyyy`, numbers
 * `Standard`/2dp right-aligned.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'sale_contract', label: 'Contract No', width: 130 },
  { key: 'delivery_order', label: 'DO' },
  { key: 'booking_no', label: 'Booking No.' },
  { key: 'client', label: 'Client' },
  { key: 'contract_quality', label: 'Contract Quality', filter: 'checkboxes' },
  { key: 'm_date', label: 'M. Date', kind: 'date' },
  { key: 'from_warehouse', label: 'From WH' },
  { key: 'to_warehouse', label: 'To WH' },
  { key: 'm_truck_no', label: 'M. Truck No' },
  { key: 'm_net_weight', label: 'M. Net weight', kind: 'number', align: 'right' },
  { key: 'container_no', label: 'Container No.' },
  { key: 'seal_no', label: 'Seal No.' },
  { key: 'truck_name', label: 'Trucking Co.' },
  { key: 'number_bags', label: 'No. bag', kind: 'number', align: 'right' },
  { key: 'tare_weight', label: 'Tare weight', kind: 'number', align: 'right' },
  { key: 'gross_weight', label: 'Gross weight', kind: 'number', align: 'right' },
  { key: 'tare_carton_pallet', label: 'Tare carton and pallet', kind: 'number', align: 'right' },
  { key: 'tare_cont', label: 'Tare cont', kind: 'number', align: 'right' },
  { key: 'max_gross_weight', label: 'Max GW', kind: 'number', align: 'right' },
  { key: 'vgm', label: 'VGM', kind: 'number', align: 'right' },
  { key: 'receipt_no', label: 'Receipt no.' },
  { key: 'gdn_or_rdn', label: 'GDN/RDN?', filter: 'checkboxes' },
  { key: 'vgm_date', label: 'Date', kind: 'date' },
  { key: 'from_supplier', label: 'From supplier' },
  { key: 'to_client', label: 'To client' },
  { key: 'receipt_quality', label: 'Receipt Quality', filter: 'checkboxes' },
  { key: 'net_delivered_weight', label: 'Net delivered weight', kind: 'number', align: 'right' },
  { key: 'no_bag_delivered', label: 'No.bag delivered', kind: 'number', align: 'right' },
  { key: 'net_weight_at_buyer', label: 'Net weight at buyer', kind: 'number', align: 'right' },
  { key: 'deduction_at_buyer_kg', label: 'Deduction (kg)', kind: 'number', align: 'right' },
  { key: 'deduction_at_buyer_vnd', label: 'Deduction (Vnđ)', kind: 'number', align: 'right' },
  { key: 'payment_weight', label: 'Payment weight', kind: 'number', align: 'right' },
  { key: 'accumulate_allocated_bag', label: 'Accumulate allocated bag', kind: 'number', align: 'right' },
  { key: 'accumulate_allocated_kg', label: 'Accumulate allocated kg', kind: 'number', align: 'right' },
  { key: 'can_be_allocated_bag', label: 'Can be allocated bag', kind: 'number', align: 'right' },
  { key: 'can_be_allocated_kg', label: 'Can be allocated kg', kind: 'number', align: 'right' },
  { key: 'allocating_bag', label: 'Allocating bag', kind: 'number', align: 'right' },
  { key: 'allocating_quantity', label: 'Allocating Quantity', kind: 'number', align: 'right' },
  { key: 'balance_bag', label: 'Balance bag', kind: 'number', align: 'right' },
  { key: 'balance_kg', label: 'Balance kg', kind: 'number', align: 'right' },
  { key: 'weight_loss', label: 'Weight loss', kind: 'number', align: 'right' },
  { key: 'note', label: 'Note', wrap: true, width: 220 },
  { key: 'date_update', label: 'Update Date', kind: 'datetime' },
  { key: 'person_update', label: 'Update Person' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleAllocationRow[], key: keyof SaleAllocationRow): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SaleControlSalesAllocationViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export { ALL_ROWS_PAGE_SIZE }

export default function SaleControlSalesAllocationView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: SaleControlSalesAllocationViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleAllocationRow | null>(null)
  const [deleteSaleAllocation, { isLoading: deleting }] = useDeleteSaleAllocationMutation()
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy' } | null>(null)
  const [massUploadOpen, setMassUploadOpen] = useState(false)
  const [editBookingOpen, setEditBookingOpen] = useState(false)
  const [editSameDoNo, setEditSameDoNo] = useState<string | null>(null)
  const [vgmDoNo, setVgmDoNo] = useState<string | null>(null)
  const [bbgnTarget, setBbgnTarget] = useState<{ deliveryOrder: string; saleContract: string } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-allocation.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleAllocationRow> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleAllocationRow
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'sales-allocation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-allocation.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetCtrlSalesSaleAlcQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(shipmentPeriodStart ? { startDate: shipmentPeriodStart } : {}),
      ...(shipmentPeriodEnd ? { endDate: shipmentPeriodEnd } : {}),
      isFinalized: finalizedFilter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => (list?.items ?? []) as unknown as SaleAllocationRow[], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleAllocationRow) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleAllocationRow | null>(
    () => rows.find((r) => r.sales_allocation_scs_id === selectedId) ?? null,
    [rows, selectedId],
  )

  // ── Toolbar actions (Access `frmCtrlSalesMainSubFormSaleAlc` FormHeader buttons,
  //    ordered by Left). Frame stage: row-selection + permission contract is wired;
  //    each action surfaces its Access target so the dialogs/reports get built next.
  //    Row-required (Access `Form![subForm]!…` guard): Edit / Copy / Delete /
  //    Edit-similar-DO / Print-VGM / Print-BBGN. No row needed: New / Mass Upload /
  //    Edit (Booking).
  const requireRow = (): SaleAllocationRow | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // btnNew / btnEdit / btnCopy → open `frmCtrlSalesQrySaleAlcAdd` (SalesAllocationFormDialog).
  // Edit/Copy require a selected row (Access guard); the dialog reads the row by its PK.
  const openForm = (mode: 'new' | 'edit' | 'copy') => {
    if (!canEdit) return
    if ((mode === 'edit' || mode === 'copy') && !requireRow()) return
    setFormState({ mode })
  }
  const handleNew = () => openForm('new')
  const handleEdit = () => openForm('edit')
  const handleCopy = () => openForm('copy')
  // btnInsert "Mass Upload" → opens `frmCtrlSalesQrySaleAlcAddMany` (no row).
  const handleMassUpload = () => {
    if (!canEdit) return
    setMassUploadOpen(true)
  }
  // btnEditMany "Edit (similar DO)" → opens `frmCtrlSalesQrySaleAlc_Edit_SameDO`
  //   for the selected row's delivery_order.
  const handleEditSimilarDo = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    if (!row.delivery_order) {
      snack.warning('Do no is empty!')
      return
    }
    setEditSameDoNo(row.delivery_order)
  }
  // btnEditBooking "Edit (Booking)" → opens `frmCtrlSalesQrySaleAlc_EditBK_Add` (no row).
  const handleEditBooking = () => {
    if (!canEdit) return
    setEditBookingOpen(true)
  }
  // btnPrintVGM "Print - VGM" → chooser `frmCtrlSalesVGMPr_Choose` for delivery_order.
  // Chooser chỉ chọn container để in + preview GET (read-only, không ghi DB) →
  // account View vẫn xem được; nút Print (in thật) trong dialog tự ẩn khi !canEdit.
  const handlePrintVgm = () => {
    const row = requireRow()
    if (!row) return
    if (!row.delivery_order) {
      snack.warning('Do no is empty!')
      return
    }
    setVgmDoNo(row.delivery_order)
  }
  // btnPrintBBGN "Print - BBGN" → report `rpCtrlSalesBBGN` for delivery_order + sale_contract.
  // Print Preview read-only (GET) → account View vẫn xem được; nút Print (in
  // thật) trong dialog tự ẩn khi !canEdit.
  const handlePrintBbgn = () => {
    const row = requireRow()
    if (!row) return
    if (!row.delivery_order) {
      snack.warning('Delivery order must be not null!')
      return
    }
    setBbgnTarget({ deliveryOrder: row.delivery_order, saleContract: row.sale_contract ?? '' })
  }

  // btnDelete → soft-delete (`is_status = 1`) the selected row. TODO BE mutation.
  const handleDeleteClick = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    setConfirmDelete(row)
  }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleAllocation(confirmDelete.sales_allocation_scs_id).unwrap()
      snack.success('Successfully Deleted')
      if (selectedId === confirmDelete.sales_allocation_scs_id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete sales allocation failed'))
    }
  }

  const cellValue = (row: SaleAllocationRow, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Button order follows Access `frmCtrlSalesMainSubFormSaleAlc` (Left order):
                New · Edit · Copy · Mass Upload · Edit (similar DO) · Edit (Booking) ·
                Delete · Print-VGM · Print-BBGN. */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleNew}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleCopy}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleMassUpload}
              startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Mass Upload"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Mass Upload</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleEditSimilarDo}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit (similar DO)"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Edit (similar DO)</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleEditBooking}
              startIcon={<EventNoteOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit (Booking)"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Edit (Booking)</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={handlePrintVgm}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - VGM"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>VGM
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={handlePrintBbgn}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - BBGN"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>BBGN
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Sales Allocation.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract, DO, client, container..."
              label="Search sales allocation"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Sales Allocation"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading || isFetching ? (
            // Reload/refetch: RTK giữ data cũ + chỉ set isFetching → phải show
            // Loading (KHÔNG để bảng cũ đứng yên) tới khi API response mới về.
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Sales Allocation found"
              description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleAllocationRow) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.sales_allocation_scs_id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Sales Allocation"
        message={confirmDelete ? `Do you want to delete Sales Allocation with ID = '${confirmDelete.sales_allocation_scs_id}'?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
      {formState && (
        <SalesAllocationFormDialog
          open
          mode={formState.mode}
          saleAllocationId={formState.mode === 'new' ? null : selectedRow?.sales_allocation_scs_id ?? null}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <SalesAllocationMassUploadDialog
        open={massUploadOpen}
        canEdit={canEdit}
        onClose={() => setMassUploadOpen(false)}
        onUploaded={() => setSelectedId(null)}
      />
      <SalesAllocationEditBookingDialog
        open={editBookingOpen}
        canEdit={canEdit}
        onClose={() => setEditBookingOpen(false)}
        onSaved={() => setSelectedId(null)}
      />
      {editSameDoNo != null && (
        <SalesAllocationEditSameDoDialog
          open
          doNo={editSameDoNo}
          canEdit={canEdit}
          onClose={() => setEditSameDoNo(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      {vgmDoNo != null && (
        <SalesAllocationVgmPrintDialog
          open
          doNo={vgmDoNo}
          canEdit={canEdit}
          onClose={() => setVgmDoNo(null)}
        />
      )}
      {bbgnTarget != null && (
        <SalesAllocationBbgnPrintDialog
          open
          deliveryOrder={bbgnTarget.deliveryOrder}
          saleContract={bbgnTarget.saleContract}
          canEdit={canEdit}
          onClose={() => setBbgnTarget(null)}
        />
      )}
    </Box>
  )
}
